import React, { useState } from "react";
import axios from "axios";
import { Alert, Button, Col, Form, Row, Spinner } from "react-bootstrap";

const API_URL = process.env.REACT_APP_API_URL;

const AiProviderFields = ({ value, onChange, disabled = false }) => {
  const config = value || {};
  const [models, setModels] = useState([]);
  const [loadingModels, setLoadingModels] = useState(false);
  const [testing, setTesting] = useState(false);
  const [feedback, setFeedback] = useState({ variant: "", text: "" });

  const update = (patch) => onChange({ ...config, ...patch });

  const readError = (error, fallback) =>
    error.response?.data?.message ||
    error.response?.data ||
    error.message ||
    fallback;

  const handleFetchModels = async () => {
    setFeedback({ variant: "", text: "" });
    setLoadingModels(true);
    try {
      const res = await axios.post(`${API_URL}/ai/models`, {
        base_url: config.base_url,
        api_key: config.api_key,
      });
      const list = res.data?.models || [];
      setModels(list);
      if (list.length > 0 && !config.model) {
        update({ model: list[0] });
      }
      setFeedback(
        list.length > 0
          ? { variant: "success", text: `Lấy được ${list.length} model.` }
          : {
              variant: "warning",
              text: "Provider không trả về model nào, hãy nhập tay.",
            },
      );
    } catch (error) {
      setModels([]);
      setFeedback({
        variant: "danger",
        text: readError(error, "Không lấy được danh sách model."),
      });
    } finally {
      setLoadingModels(false);
    }
  };

  const handleTest = async () => {
    setFeedback({ variant: "", text: "" });
    setTesting(true);
    try {
      const res = await axios.post(`${API_URL}/ai/test`, {
        base_url: config.base_url,
        api_key: config.api_key,
        model: config.model,
      });
      setFeedback({
        variant: "success",
        text: `Kết nối OK với model ${res.data?.model} (${res.data?.latency_ms} ms).`,
      });
    } catch (error) {
      setFeedback({
        variant: "danger",
        text: readError(error, "Không kết nối được tới provider."),
      });
    } finally {
      setTesting(false);
    }
  };

  return (
    <div className="border rounded p-3 mb-3 bg-light">
      <div className="d-flex justify-content-between align-items-center mb-2">
        <strong>Cấu hình AI</strong>
        <small className="text-muted">
          Không lưu lại, nhập lại mỗi phiên làm việc.
        </small>
      </div>

      <Row className="g-2">
        <Col xs={12} md={6}>
          <Form.Group>
            <Form.Label>Base URL</Form.Label>
            <Form.Control
              type="text"
              placeholder="https://openrouter.ai/api/v1"
              value={config.base_url || ""}
              onChange={(e) => update({ base_url: e.target.value })}
              disabled={disabled}
            />
            <Form.Text className="text-muted">
              Base thuần, hệ thống tự thêm /chat/completions và /models.
            </Form.Text>
          </Form.Group>
        </Col>

        <Col xs={12} md={6}>
          <Form.Group>
            <Form.Label>API key</Form.Label>
            <Form.Control
              type="password"
              autoComplete="off"
              placeholder="sk-..."
              value={config.api_key || ""}
              onChange={(e) => update({ api_key: e.target.value })}
              disabled={disabled}
            />
          </Form.Group>
        </Col>

        <Col xs={12} md={6}>
          <Form.Group>
            <Form.Label>Model</Form.Label>
            <Form.Control
              type="text"
              list="ai-model-options"
              placeholder="Chọn hoặc nhập tay"
              value={config.model || ""}
              onChange={(e) => update({ model: e.target.value })}
              disabled={disabled}
            />
            <datalist id="ai-model-options">
              {models.map((model) => (
                <option key={model} value={model} />
              ))}
            </datalist>
          </Form.Group>
        </Col>

        <Col xs={12} md={6}>
          <Form.Group className="d-flex flex-column h-100 justify-content-end">
            <Form.Check
              type="switch"
              id="ai-json-mode"
              label="Gửi response_format JSON (tắt nếu provider không hỗ trợ)"
              checked={Number(config.json_mode) !== 0}
              onChange={(e) => update({ json_mode: e.target.checked ? 1 : 0 })}
              disabled={disabled}
            />
          </Form.Group>
        </Col>
      </Row>

      <div className="d-flex flex-wrap gap-2 mt-3">
        <Button
          type="button"
          variant="outline-primary"
          size="sm"
          onClick={handleFetchModels}
          disabled={disabled || loadingModels || !config.base_url || !config.api_key}
        >
          {loadingModels ? <Spinner animation="border" size="sm" /> : "Lấy danh sách model"}
        </Button>
        <Button
          type="button"
          variant="outline-secondary"
          size="sm"
          onClick={handleTest}
          disabled={
            disabled || testing || !config.base_url || !config.api_key || !config.model
          }
        >
          {testing ? <Spinner animation="border" size="sm" /> : "Kiểm tra kết nối"}
        </Button>
      </div>

      {feedback.text && (
        <Alert variant={feedback.variant || "info"} className="mt-3 mb-0 py-2">
          {feedback.text}
        </Alert>
      )}
    </div>
  );
};

export default AiProviderFields;
